import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { MemberDeletedRestorable } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import MemberForm, { IMemberFormData, MemberGroupChanges } from '../../components/forms/MemberForm';
import GroupChangePrompts, { type GroupChangeOp, groupChangeOps } from '../../components/GroupChangePrompts';
import { ConfirmModal } from '../../components/ui';
import { translateError } from '../../utils/errorMessages';

const MemberCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 409 MEMBER_DELETED_RESTORABLE: the birth number belongs to a tombstone.
  // Restoring keeps their payment history; creating a duplicate cannot.
  const [restorable, setRestorable] = useState<MemberDeletedRestorable['deletedMember'] | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [groupOps, setGroupOps] = useState<GroupChangeOp[] | null>(null);
  const [createdName, setCreatedName] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const submitHandler = async (data: IMemberFormData, groupChanges: MemberGroupChanges) => {
    setLoading(true);
    setError(null);

    try {
      const { data: created } = await api.members.create(data);
      setLoading(false);
      addToast(t('members.createTitle') + ' - ' + t('common.save') + '!', 'success');
      // Group adds go through the D6 prompt; leave once it is done.
      const ops = groupChangeOps(Number(created?._id), groupChanges);
      if (ops.length > 0) {
        setCreatedName({ [String(created._id)]: created.name ?? '' });
        setGroupOps(ops);
      } else {
        navigate('/admin/members');
      }
    } catch (err: any) {
      const body = err?.response?.data as MemberDeletedRestorable | undefined;
      if (err?.response?.status === 409 && body?.code === 'MEMBER_DELETED_RESTORABLE') {
        setRestorable(body.deletedMember);
        setLoading(false);
        return;
      }
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restorable) return;
    setRestoring(true);
    try {
      await api.members.restore(restorable._id);
      addToast(t('dataSafety.restoreSuccess'), 'success');
      navigate(`/admin/members/${restorable._id}/edit`);
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
    } finally {
      setRestoring(false);
      setRestorable(null);
    }
  };

  return (
    <div className="admin-form-page">
      <h2>{t('members.createTitle')}</h2>
      {error && <p className="error-message">{t('common.error')}: {error}</p>}
      <MemberForm
        onSubmit={submitHandler}
        loading={loading}
        submitButtonText={t('members.createButton')}
      />
      <Link to="/admin/members" className="form-link">{t('members.goBack')}</Link>

      <GroupChangePrompts
        ops={groupOps}
        memberNames={createdName}
        onDone={() => {
          setGroupOps(null);
          navigate('/admin/members');
        }}
      />

      <ConfirmModal
        isOpen={!!restorable}
        onClose={() => setRestorable(null)}
        onConfirm={handleRestore}
        title={t('dataSafety.restoreTitle')}
        message={t('dataSafety.restoreMessage', {
          name: restorable?.name ?? '',
          payments: restorable?.retainedPayments ?? 0,
        })}
        confirmText={t('dataSafety.restoreConfirm')}
        cancelText={t('common.cancel')}
        loading={restoring}
      />
    </div>
  );
};

export default MemberCreatePage;
